'use client';

import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePathname, useRouter } from 'next/navigation';
import { Loading } from '@/components/ui/loading'; // Assuming a Loading component exists

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  // ADDED: Log component mount
  console.log('[ProtectedRoute] Component mounted/rendered'); 

  const { user, loading, canAccessPage, error } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  // ADDED: State to track if the initial auth check has completed
  const [initialAuthChecked, setInitialAuthChecked] = React.useState(false);
  
  // Add debug log
  const logProtected = (message: string) => {
    console.log(`[ProtectedRoute] ${message} | user: ${!!user} | loading: ${loading} | initialAuthChecked: ${initialAuthChecked}`);
  };

  React.useEffect(() => {
    console.log(`[ProtectedRoute] useEffect run. Path: ${pathname}, loading: ${loading}, user: ${!!user}, initialAuthChecked: ${initialAuthChecked}`);

    // Check for bypass mode
    const isAuthBypassed = typeof window !== 'undefined' && (
      localStorage.getItem('auth_bypass_active') === 'true' || 
      window.forceDashboardRender === true
    );
    
    if (isAuthBypassed) {
      logProtected('AUTH BYPASS MODE DETECTED - Skipping protection checks');
      setInitialAuthChecked(true);
      return;
    }

    // Mark initial check as complete once loading becomes false for the first time.
    // This signifies the initial attempt to determine auth state is done.
    if (!loading && !initialAuthChecked) {
        logProtected('Initial auth determination complete (loading is false). Setting initialAuthChecked true.');
        setInitialAuthChecked(true);
    }

    // Skip checks for public routes
    const publicPaths = ['/auth/signin', '/']; 
    if (publicPaths.includes(pathname)) {
      // Allow public paths immediately, don't wait for initialAuthChecked
      logProtected('Public path, access granted.');
      return;
    }

    // Set a timeout to ensure we don't get stuck
    const timerId = setTimeout(() => {
      if (!initialAuthChecked) {
        logProtected('TIMEOUT - Still waiting for auth check after 5 seconds. Forcing initialAuthChecked true.');
        setInitialAuthChecked(true);
      }
    }, 5000);

    // Only perform redirects *after* the initial auth determination is complete
    // AND ensure loading is false to avoid redirects during background refreshes.
    if (initialAuthChecked && !loading) {
      const isPublic = publicPaths.includes(pathname);
      
      if (!user && !isPublic) {
        // If no user and not on a public path, redirect to sign-in
        logProtected('Initial check done, not loading. No user, not public path. Redirecting to sign-in.');
        const redirectUrl = `/auth/signin?redirect=${encodeURIComponent(pathname)}`;
        router.replace(redirectUrl);
      } else if (user && !isPublic && !canAccessPage(pathname)) {
        // If user exists, not public, but lacks access, redirect to dashboard
        logProtected(`Initial check done, not loading. Access denied for path: ${pathname}. Redirecting to dashboard.`);
        router.replace('/dashboard'); 
      } else if (user && !isPublic) {
         // User exists, not public, and has access
         logProtected(`Initial check done, not loading. User exists, access granted for path: ${pathname}`);
      } else if (isPublic) {
        // Path is public, access granted
        logProtected(`Initial check done, not loading. Public path ${pathname} allowed.`);
      }
    } else if (initialAuthChecked && loading) {
      // Log if we are skipping redirect because loading is true after initial check
      logProtected('Initial check done, but loading is true. Skipping redirect logic for now.');
    }
    
    // Clear timeout on cleanup
    return () => clearTimeout(timerId);
  }, [user, loading, pathname, canAccessPage, router, initialAuthChecked]);

  // Show loading indicator ONLY until the initial determination is complete
  if (!initialAuthChecked) { 
    return (
      <div className="flex h-screen w-full items-center justify-center">
        {/* Base message on context loading state during initial check */}
        {/* We always show some loading text here because initialAuthChecked is false */}
        <Loading size="lg" text="Loading..." />
      </div>
    );
  }

  // After initial check, render children only if access is permitted OR path is public
  const publicPaths = ['/auth/signin', '/'];
  const isPublicPath = publicPaths.includes(pathname);
  
  // MODIFIED: Also check if loading is false before rendering children after initial check
  // This prevents rendering content briefly if a background refresh immediately follows the initial check.
  if (!loading && (isPublicPath || (user && canAccessPage(pathname)))) {
     logProtected('Rendering children (initial check done, not loading, access permitted or public path).');
     return <>{children}</>;
  }
  
  // If loading is still true after initial check, continue showing a loading indicator
  if (loading) {
    logProtected('Initial check done, but still loading. Showing loading indicator.');
    return (
      <div className="flex h-screen w-full items-center justify-center">
        {/* Show a generic loading message during subsequent loads */}
        <Loading size="lg" text="Loading page..." /> 
      </div>
    );
  }

  // If initial check is done, not loading, but conditions aren't met (e.g., redirecting), render null.
  logProtected('Conditions not met after initial check and loading finished. Rendering null (likely redirecting).');
  return null; 
}; 